'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();

import { clsx } from 'clsx/lite'

import { AppUiVisual } from '../app-ui-visual'
import { API_KEY, client, RIVIERWIJK_SHAREHOLDERS, teamMember } from '../demo-data'
import { useVisualCopy } from '../visual-copy'
import { AppHeader, AppScreen, HeaderStat } from './app-screen'
import styles from './integrations-visual.module.css'
import { useScenario } from './use-scenario'

// The 4 connections + MCP chat settle by beat 7, then the full "everything active" state rests
// before the loop wraps back to the empty start.
const SUBJECT = client('10040')
/** The colleague on that client's record — the second answer, read off the client list's own column. */
const MANAGER = teamMember(SUBJECT.manager)

const TOTAL_BEATS = 12
const REST_BEAT = 9 // SSR / reduced-motion snapshot — all four live, both questions answered
// Adopts the siblings' clearing-beat idiom (it used to hard-wrap 11 -> 0): the final beat renders
// as the animated collapse into the start state, which also makes it the fast-start entry beat.
const RESET_BEAT = 11

const KVK_AT = 1
const API_AT = 2
const MCP_AT = 3
const CHAT_AT = 5
const SECOND_CHAT_AT = 7

/** Resolves `beat` (possibly SSR/reduced-motion `null`) to the beat used to derive card state. */
function effectiveBeat(beat: number | null): number {
  if (beat === null) return REST_BEAT
  return beat >= RESET_BEAT ? 0 : beat
}

function StatusDot({ active, activeLabel }: { active: boolean; activeLabel?: string }) {
  const { t } = useVisualCopy()
  return (
    <span className="flex shrink-0 items-center gap-1 text-[8px] font-bold whitespace-nowrap">
      <span className={clsx('size-[6px] rounded-full', styles.dot, active && styles.dotActive)} />
      <span className={clsx(styles.status, active && styles.statusActive)}>
        {active ? (activeLabel ?? t.screen.integrations.active) : t.screen.integrations.connecting}
      </span>
    </span>
  )
}

/**
 * Koppelingen & AI visual — three connections coming up in turn: the KvK, the open API
 * (which hands out the key an integration authenticates with), and MCP, whose mini chat then
 * answers two questions about the same client the section's other cards show. Driven by
 * `useScenario`'s 12-beat loop; `beat === null` (SSR, no-JS, reduced motion) renders the resolved
 * rest state.
 */
export function IntegrationsVisual({ className }: { className?: string }) {
  const { t } = useVisualCopy()
  const { ref, beat } = useScenario(TOTAL_BEATS, { intervalMs: 1000, restBeat: REST_BEAT })
  const effBeat = effectiveBeat(beat)

  const kvkActive = effBeat >= KVK_AT
  const apiActive = effBeat >= API_AT
  const mcpActive = effBeat >= MCP_AT
  const chatVisible = effBeat >= CHAT_AT
  const secondChatVisible = effBeat >= SECOND_CHAT_AT

  return (
    <AppUiVisual label={t.screen.integrations.caption} className={className}>
      <AppScreen>
        <div ref={ref} data-visual="integrations" className="flex h-full flex-col">
          <AppHeader
            title={t.screen.integrations.screenTitle}
            detail={t.screen.integrations.title}
            right={<HeaderStat value="3" label={t.screen.integrations.activeConnections} />}
          />

          <div className="flex flex-1 flex-col justify-center gap-2 px-3 py-2.5">
            <div
              className={clsx(
                'flex items-center gap-2.5 rounded-[9px] px-2.5 py-2',
                styles.card,
                kvkActive && styles.cardActive,
              )}
            >
              <span
                className="flex size-6 shrink-0 items-center justify-center rounded-[6px] text-[10px] font-extrabold text-white"
                style={{ background: '#5b3f8f' }}
              >
                K
              </span>
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="text-[11px] font-bold whitespace-nowrap">{t.screen.integrations.kvkIntegration}</span>
                <span className="text-[8.5px]" style={{ color: 'var(--au-text2)' }}>
                  {t.screen.integrations.kvkBody}
                </span>
              </div>
              <StatusDot active={kvkActive} />
            </div>

            <div
              className={clsx(
                'flex flex-col gap-2 rounded-[9px] px-2.5 py-2',
                styles.card,
                apiActive && styles.cardActive,
              )}
            >
              <div className="flex items-center gap-2.5">
                <span
                  className="flex size-6 shrink-0 items-center justify-center rounded-[6px]"
                  style={{ background: 'var(--au-field)', color: 'var(--au-text)' }}
                >
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M9 8l-4 4 4 4M15 8l4 4-4 4" />
                  </svg>
                </span>
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="text-[11px] font-bold whitespace-nowrap">{t.screen.integrations.openApi}</span>
                  <span className="text-[8.5px]" style={{ color: 'var(--au-text2)' }}>
                    {t.screen.integrations.apiBody}
                  </span>
                </div>
                <StatusDot active={apiActive} />
              </div>
              {/* What "connected" means for an API: the key an integration authenticates with —
                  inside the connection's own card, because it belongs to that connection. */}
              <div className={clsx(styles.keyRow, apiActive && styles.keyRowOn)}>
                <span className={styles.keyLabel}>{t.screen.integrations.apiKey}</span>
                <code className={styles.key}>{API_KEY}</code>
                <span className={styles.keyCopy}>{t.screen.integrations.copy}</span>
              </div>
            </div>

            <div
              className={clsx(
                'flex flex-col gap-2 rounded-[9px] px-2.5 py-2',
                styles.card,
                mcpActive && styles.cardActive,
              )}
            >
              <div className="flex items-center gap-2.5">
                <span
                  className="flex size-6 shrink-0 items-center justify-center rounded-[6px]"
                  style={{ background: 'var(--au-accent-soft)', color: 'var(--au-accent)' }}
                >
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M12 3l1.9 4.6L18.5 9l-4.6 1.9L12 15.5 10.1 10.9 5.5 9l4.6-1.4L12 3Z" />
                  </svg>
                </span>
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="text-[11px] font-bold whitespace-nowrap">{t.screen.integrations.mcp}</span>
                  <span className="text-[8.5px]" style={{ color: 'var(--au-text2)' }}>
                    {t.screen.integrations.mcpBody}
                  </span>
                </div>
                <StatusDot active={mcpActive} activeLabel={t.screen.integrations.connected} />
              </div>
              <div className={clsx('flex flex-col gap-1.5', styles.chat, chatVisible && styles.chatVisible)}>
                <div
                  className="max-w-[90%] self-end rounded-[10px] rounded-br-[3px] px-2.5 py-1.5 text-[10px] leading-snug text-white"
                  style={{ background: 'var(--au-accent)' }}
                >
                  {t.screen.integrations.holdingQuestion(SUBJECT.short)}
                </div>
                <div
                  className="max-w-[96%] self-start rounded-[10px] rounded-bl-[3px] px-2.5 py-1.5 text-[10px] leading-snug"
                  style={{ background: 'var(--au-field)', color: 'var(--au-text)' }}
                >
                  {t.screen.integrations.holdingsAnswer(RIVIERWIJK_SHAREHOLDERS.length)}{' '}
                  {RIVIERWIJK_SHAREHOLDERS.map((shareholder, index) => (
                    <span key={shareholder.nr}>
                      {index > 0 && (index === RIVIERWIJK_SHAREHOLDERS.length - 1 ? t.chat.and : t.chat.comma)}
                      <b className="font-semibold">{client(shareholder.nr).short}</b> ({shareholder.pct}%)
                    </span>
                  ))}
                  .
                </div>
              </div>
              <div className={clsx('flex flex-col gap-1.5', styles.chat, secondChatVisible && styles.chatVisible)}>
                <div
                  className="max-w-[90%] self-end rounded-[10px] rounded-br-[3px] px-2.5 py-1.5 text-[10px] leading-snug text-white"
                  style={{ background: 'var(--au-accent)' }}
                >
                  {t.screen.integrations.sampleQuestion}
                </div>
                <div
                  className="max-w-[96%] self-start rounded-[10px] rounded-bl-[3px] px-2.5 py-1.5 text-[10px] leading-snug"
                  style={{ background: 'var(--au-field)', color: 'var(--au-text)' }}
                >
                  <b className="font-semibold">{MANAGER.name}</b> — {t.teamRole[MANAGER.role].toLowerCase()}.
                </div>
              </div>
            </div>
          </div>
        </div>
      </AppScreen>
    </AppUiVisual>
  )
}
